/**
 * @file useLiveUsage.ts
 * @brief The recorder's live included-minutes signal (B5, 22-10-2026), web
 *        and Mac: "N included minutes left" at start, and one notice when
 *        this recording uses the last of them.
 *
 * @details
 * Polls `GET /billing/usage/live?tier=&elapsedSeconds=` at start and then
 * every `pollSeconds` (60) while recording or on hold. Display only: it never
 * stops, pauses or slows the recorder (the server never cuts a recording).
 * 404 (backend without B5), offline or any error = no signal: no line, no
 * notice, recording goes on. A 404 stops polling for this recording.
 * The notice is the server's `message` plus `priceNote` (web may name a
 * price; the phone may not, ADR-043), shown once per recording.
 * Fair use (B6): `nextRecordingBlocked` re-reads the plan status so the
 * Notes start gate follows `fairUse.blocked` after this meeting.
 *
 * Related: ../my-billing/freeMinutes.ts (includedLeftLine, includedUsedNotice)
 *
 * @author DES
 * @date 22-10-2026
 */

import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiClientError, apiGet } from "@/shared/api/client";
import { billingEndpoints } from "@/shared/api/endpoints";
import { toast } from "@/shared/hooks/useToast";
import { includedUsedNotice, livePollMs, type LiveUsage } from "@/features/my-billing/freeMinutes";
import { myBillingKeys } from "@/features/my-billing/api";

export type LiveUsageFetcher = (tier: "full" | "notes", elapsedSeconds: number) => Promise<LiveUsage>;

const fetchLiveUsage: LiveUsageFetcher = (tier, elapsedSeconds) =>
  apiGet<LiveUsage>(billingEndpoints.usageLive, { params: { tier, elapsedSeconds } });

function isNotFound(err: unknown): boolean {
  if (err instanceof ApiClientError && err.status === 404) return true;
  const status = (err as { response?: { status?: number } } | null)?.response?.status;
  return status === 404;
}

export function useLiveUsage({
  tier,
  active,
  elapsedMs,
  fetcher = fetchLiveUsage,
}: {
  tier: "full" | "notes";
  /** Recording or on hold (and not the UI preview, unless it passes a stub). */
  active: boolean;
  elapsedMs: number;
  fetcher?: LiveUsageFetcher;
}): LiveUsage | null {
  const [live, setLive] = useState<LiveUsage | null>(null);
  const elapsedRef = useRef(elapsedMs);
  elapsedRef.current = elapsedMs;
  const noticeShownRef = useRef(false);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const qc = useQueryClient();

  useEffect(() => {
    if (!active) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let last: LiveUsage | null = null;

    const poll = async () => {
      try {
        const next = await fetcherRef.current(tier, Math.max(0, Math.floor(elapsedRef.current / 1000)));
        if (stopped) return;
        last = next;
        setLive(next);
        const notice = includedUsedNotice(next);
        if (notice && !noticeShownRef.current) {
          noticeShownRef.current = true;
          toast({ title: "Still recording", description: notice });
          // Fair use reached (B6): Home and Setup re-read `fairUse.blocked`
          // so the next Notes start is gated; this recording carries on.
          if (next.events?.some((e) => e?.nextRecordingBlocked)) {
            void qc.invalidateQueries({ queryKey: myBillingKeys.status() });
          }
        }
      } catch (err) {
        // No signal: keep recording, keep the last numbers (or none).
        if (stopped || isNotFound(err)) return;
      }
      if (!stopped) timer = setTimeout(() => void poll(), livePollMs(last));
    };
    void poll();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [active, tier, qc]);

  return live;
}
